// React & Nextjs imports
import React from "react";

// Third party imports
import { format, subDays } from "date-fns";

// Models & Constants
import { FUNDAMENTAL_TABS, UsStock } from "@/app/interface/global";

// Hooks & Utils
// import { formatAmountToUS /* , formatCurrency */ } from "@/app/utils/currency-formatter";
import { useNewsAndEventsQuery, useSimilarStocksQuery } from "@/app/global/use-us-stocks";

// Components
import KeyFinancials from "@/app/global/components/key-and-financials";
import SimilerStocks from "@/app/global/components/similer-stocks";
import NewsAndEvents from "@/app/global/components/news-and-events";
// import StocksFaqs from "@/app/global/components/stocks-faqs";
import { useCulture } from "@/context/cultureProvider";
import { CountryCurrency } from "@/app/interface/currency";
import { useParams } from "next/navigation";
import { productRoute } from "@/types/product";
import { ProductRoute } from "@/types/product";
// import OnTheApp from "./on-the-app";
// import ImgNewsAndEventsBlur from "@/app/assets/images/news-and-events-blur.png";
// import ImgSimilarStocks from "@/app/assets/images/similar-stocks-blur.png";

interface MetricProps {
    label: string;
    value: string | number;
}

const Metric: React.FC<MetricProps> = ({ label, value }) => (
    <div className="flex flex-col gap-1 sm:gap-2">
        <span className="text-sm text-[#9195A9]">{label}</span>
        <span className="-mt-1 inline-block text-base font-medium text-black sm:text-lg">
            {value}
        </span>
    </div>
);

interface FinancialMetricsProps {
    stockDetails: UsStock;
}

interface FundamentalsSectionProps {
    stockDetails: UsStock;
    culture: CountryCurrency;
}

const FundamentalsSection: React.FC<FundamentalsSectionProps> = ({
    stockDetails /*, culture */,
}) => {
    return (
        <div className="grid grid-cols-2 gap-x-8 gap-y-6 rounded-xl border px-4 py-5 sm:grid-cols-3 sm:gap-x-16 sm:gap-y-8 md:border-0 md:px-0 md:py-0">
            <Metric
                label="Market Cap"
                value={
                    /* `${formatCurrency(stockDetails?.company_fundamentals?.market_cap, culture.currencyCode, culture.locale) || "--"}` */ stockDetails
                        ?.company_fundamentals?.market_cap
                        ? stockDetails.company_fundamentals.market_cap
                        : "--"
                }
            />
            <Metric
                label="Volume (Qty)"
                value={
                    stockDetails?.company_fundamentals?.volume
                        ? stockDetails.company_fundamentals.volume
                        : "--"
                }
            />
            <Metric
                label="P/E Ratio"
                value={stockDetails?.company_fundamentals?.pe_ratio || "--"}
            />
            <Metric label="EPS" value={stockDetails?.company_fundamentals?.eps || "--"} />
            <Metric
                label="Dividend yield"
                value={
                    stockDetails?.company_fundamentals?.dividend_yield
                        ? stockDetails?.company_fundamentals?.dividend_yield /* + "%" */
                        : "--"
                }
            />
        </div>
    );
};

// const productType = "unlistedEquities";
// const product = "HIDENSE";

const FinancialMetrics: React.FC<
    FinancialMetricsProps & { activeTab: string; setActiveTab: (tab: string) => void }
> = ({ stockDetails, activeTab, setActiveTab }) => {
    const { cultureContext } = useCulture();

    const params = useParams();
    const countryProduct = params?.countryProduct as ProductRoute;
    const productCode = params?.id ? params?.id[0] : null;
    // const productName = params?.id ? params.id[1] : null;
    const partialLinkUrl =
        typeof window !== "undefined" &&
        window.location.href.substring(0, window.location.href.indexOf(productCode!));

    const today = new Date();
    const thirtyDaysBack = subDays(today, 30);

    const { newsAndEvents } = useNewsAndEventsQuery({
        symbol: stockDetails?.symbol,
        from: format(today, "yyyy-MM-dd"),
        to: format(thirtyDaysBack, "yyyy-MM-dd"),
    });

    const { similarStocks } = useSimilarStocksQuery({
        symbol: stockDetails?.symbol,
    });

    const renderTabContent = () => {
        if (
            countryProduct === productRoute["us-etfs"] &&
            activeTab === FUNDAMENTAL_TABS.FUNDAMENTALS
        ) {
            return <KeyFinancials stockDetails={stockDetails} />; // default tab for ETF
        }

        switch (activeTab) {
            case FUNDAMENTAL_TABS.FUNDAMENTALS:
                return <FundamentalsSection stockDetails={stockDetails} culture={cultureContext} />;
            case FUNDAMENTAL_TABS.KEY_FINANCIALS:
                return <KeyFinancials stockDetails={stockDetails} />;
            case FUNDAMENTAL_TABS.NEWS_AND_EVENTS:
                return (
                    <>
                        {/* <OnTheApp
                            bg_src={ImgNewsAndEventsBlur}
                            title="Explore the latest market news and events on the app"
                        /> */}
                        <NewsAndEvents
                            stockDetails={stockDetails}
                            newsItems={newsAndEvents?.data?.news.slice(0, 10)}
                        />
                    </>
                );
            case FUNDAMENTAL_TABS.SIMILAR_STOCKS:
                return (
                    <>
                        {/* <OnTheApp
                            bg_src={ImgSimilarStocks}
                            title="Discover similar global stocks on the app"
                            className="h-[352px]"
                        /> */}
                        <SimilerStocks
                            similarStocks={similarStocks?.data?.collection}
                            culture={cultureContext}
                            partialLinkUrl={partialLinkUrl}
                        />
                    </>
                );
            /* case FUNDAMENTAL_TABS.FAQS:
                return (
                    <StocksFaqs
                        compact={true}
                        bgColor="bg-white"
                        productType={productType}
                        product={product}
                    />
                ); */
            default:
                <FundamentalsSection stockDetails={stockDetails} culture={cultureContext} />;
        }
    };

    return (
        <div
            className={`pb-10 ${/* isBasket */ false ? "w-full max-w-max" : ""} sm:pt-12`} /* sm:w-[832px] */
        >
            <nav className="mb-8 overflow-x-auto border-b sm:mb-6">
                <ul className="flex min-w-max gap-6 whitespace-nowrap sm:gap-8">
                    {Object.values(FUNDAMENTAL_TABS).map((tab) => {
                        if (countryProduct === productRoute["us-etfs"] && tab === "Fundamentals") {
                            return null;
                        }
                        return (
                            <li
                                key={tab}
                                className={`cursor-pointer border-b-2 px-3 pb-2.5 text-sm transition-all duration-200 ease-in md:text-base ${
                                    activeTab === tab
                                        ? "border-orange-500 text-orange-500"
                                        : "border-transparent text-gray-500"
                                }`}
                                onClick={() => setActiveTab(tab as string)}
                            >
                                {tab}
                            </li>
                        );
                    })}
                </ul>
            </nav>
            {countryProduct !== productRoute["us-etfs"] && activeTab === "Fundamentals" && (
                <h2 className="mb-6 text-3xl font-semibold xs:text-xl sm:mb-5">Fundamentals</h2>
            )}
            {renderTabContent()}
        </div>
    );
};

export default FinancialMetrics;
